import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { doctorAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';
import debounce from 'lodash/debounce';

const PatientSpecialists = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    specialization: '',
    gender: '',
    hospital_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Specializations List (Could be dynamic from API)
  const specializations = [
    'All', 'General Physician', 'Cardiologist', 'Dermatologist',
    'Pediatrician', 'Gynecologist', 'Neurologist', 'Orthopedic'
  ];

  const fetchDoctors = async (query = '', currentFilters = {}) => {
    try {
      setLoading(true);

      // Construct query params
      const params = {
        search: query,
        ...currentFilters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      const res = await doctorAPI.searchDoctors(params);

      // Handle different response structures if using fallback
      const data = res.data?.data?.doctors || [];
      setDoctors(data);
    } catch (error) {
      console.error(error);
      showError('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((query, currentFilters) => {
      fetchDoctors(query, currentFilters);
    }, 500),
    []
  );

  useEffect(() => {
    // Initial load
    // Check if searchDoctors exists, if not use old logic temp (handled in fetch)
    // Actually we need to add searchDoctors to frontend api too! 
    // I will assume I need to ADD it to frontend api in next step or now. 
    // For now I will assume it exists or fallback.

    // Actually, since I haven't added `searchDoctors` to frontend `api.js` yet, I should use `getApprovedDoctors` if needed but pass params? 
    // The previous step added `search` endpoint to backend. I need to update `api.js` to expose it.
    // I will do that in the next tool call.

    fetchDoctors(searchTerm, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]); // Trigger on filter change immediately, search uses debounce

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value, filters);
  };

  const handleFilterChange = (key, value) => {
    // If "All" is selected for specialization, clear the filter
    if (key === 'specialization' && value === 'All') {
      setFilters(prev => ({ ...prev, [key]: '' }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const bookSession = (doctorId) => {
    if (!user.subscription_status || user.subscription_status !== 'active') {
      showError('Active subscription required for sessions');
      navigate('/patient/subscription');
      return;
    }
    navigate(`/patient/consultation/book/${doctorId}`);
  };

  return (
    <div className="space-y-6 min-h-screen">
      <div className="sticky top-0 bg-dark z-10 pb-4 pt-2">
        <h1 className="text-2xl font-bold text-gold mb-4 px-2">Find Specialists</h1>

        {/* Search & Filter Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, specialist..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full bg-gray-900 rounded-xl py-3 pl-10 pr-4 text-light focus:border-gold focus:outline-none"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-3 rounded-xl border ${showFilters || Object.values(filters).some(Boolean) ? 'bg-gold border-gold text-dark' : 'bg-gray-900 border-gray-700 text-gray-400'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-900 rounded-xl animate-in fade-in slide-in-from-top-2">
            <h3 className="font-semibold text-gold mb-3">Filters</h3>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs uppercase">Specialization</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={() => handleFilterChange('specialization', '')}
                    className={`px-3 py-1 text-sm rounded-full border ${!filters.specialization || filters.specialization === 'All' ? 'bg-gold text-dark border-gold' : 'border-gray-600 text-gray-400'}`}
                  >
                    All
                  </button>
                  {specializations.filter(s => s !== 'All').map(spec => (
                    <button
                      key={spec}
                      onClick={() => handleFilterChange('specialization', spec)}
                      className={`px-3 py-1 text-sm rounded-full border ${filters.specialization === spec ? 'bg-gold text-dark border-gold' : 'border-gray-600 text-gray-400'}`}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs uppercase">Gender</label>
                <div className="flex gap-4 mt-2">
                  {['Male', 'Female'].map(g => (
                    <label key={g} className="flex items-center text-light">
                      <input
                        type="radio"
                        name="gender"
                        checked={filters.gender === g}
                        onChange={() => handleFilterChange('gender', g)}
                        className="mr-2 text-gold focus:ring-gold bg-gray-800 border-gray-600"
                      />
                      {g}
                    </label>
                  ))}
                  <label className="flex items-center text-light">
                    <input
                      type="radio"
                      name="gender"
                      checked={filters.gender === ''}
                      onChange={() => handleFilterChange('gender', '')}
                      className="mr-2 text-gold focus:ring-gold bg-gray-800 border-gray-600"
                    />
                    Any
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Swipeable Categories */}
        <div className="mt-4 overflow-x-auto hide-scrollbar pb-2 -mx-2 px-2">
          <div className="flex gap-2 w-max">
            {specializations.map(spec => (
              <button
                key={spec}
                onClick={() => handleFilterChange('specialization', spec)}
                className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full border transition-colors shadow-sm
                  ${(filters.specialization === spec || (spec === 'All' && !filters.specialization))
                    ? 'bg-gold text-dark border-gold shadow-gold/20'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gold/50 hover:bg-gray-700'
                  }`}
              >
                {spec}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No specialists found matching your criteria.</p>
          </div>
        ) : (
          doctors.map((doctor) => (
            <div key={doctor._id} className="bg-gray-800/50 backdrop-blur-md rounded-2xl p-4/50 hover:border-gold/30 transition-all shadow-lg">
              <div className="flex gap-4">
                <img
                  src={formatImageUrl(doctor.profile_image)}
                  alt={doctor.name}
                  className="w-20 h-20 rounded-xl object-cover border border-gray-600"
                />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg text-light flex items-center gap-1">
                        {doctor.name}
                        {doctor.is_blue_tick === true && (
                          <span className="ml-1 inline-flex items-center justify-center bg-blue-500/10 rounded-full p-0.5" title="Verified Specialist">
                            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </h3>
                      <span className="bg-blue-900/40 text-blue-400 text-xs px-2 py-0.5 rounded border border-blue-800">
                        {doctor.specialization}
                      </span>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="flex items-center bg-yellow-900/20 px-2 py-1 rounded text-yellow-500 text-xs font-bold border border-yellow-700/30">
                        ★ {doctor.rating > 0 ? doctor.rating.toFixed(1) : 'New'}
                      </div>
                      {doctor.ratings_count > 0 && (
                        <span className="text-[10px] text-gray-500 mt-0.5">{doctor.ratings_count} reviews</span>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-400 text-xs mt-2 line-clamp-2">
                    {doctor.bio || 'Experienced specialist delivering high-quality care.'}
                  </p>

                  {doctor.hospital && (
                    <div className="flex items-center mt-2 text-gray-500 text-xs">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                      {doctor.hospital?.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => navigate(`/patient/doctor-profile/${doctor._id}`)} // Assuming profile page exists or created
                  className="flex-1 py-3 rounded-xl border border-gray-600 text-gray-300 font-medium text-sm hover:bg-gray-700"
                >
                  View Profile
                </button>
                <button
                  onClick={() => bookSession(doctor._id)}
                  className="flex-1 py-3 rounded-xl bg-gold text-dark font-bold text-sm hover:bg-yellow-500 shadow-lg shadow-yellow-900/20"
                >
                  Start Session
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PatientSpecialists;
